import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { InvestorNote, CATEGORIA_NOTA_OPTIONS } from '../../types/InvestorNote';
import { noteService } from '../../services/noteService';
import './NoteForm.css';

interface NoteFormProps {
  investorId: string;
  note: InvestorNote | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const NoteForm: React.FC<NoteFormProps> = ({ investorId, note, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<InvestorNote>({
    investorId: investorId,
    categoria: '',
    comentario: '',
    proximoPaso: '',
    fecha: new Date().toISOString(),
    esAutomatica: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (note) {
      setFormData(note);
    }
  }, [note]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoria || !formData.comentario) {
      showToast('Por favor, completa todos los campos obligatorios', 'warning');
      return;
    }

    setLoading(true);
    try {
      if (note?.id) {
        await noteService.update(note.id, formData);
        showToast('Nota actualizada correctamente');
      } else {
        await noteService.create(investorId, formData);
        showToast('Nota creada correctamente');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving note:', error);
      showToast('Error al guardar la nota', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="note-form">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group-inline">
            <label htmlFor="categoria">
              Categoría <span className="required">*</span>
            </label>
            <select
              id="categoria"
              name="categoria"
              value={formData.categoria}
              onChange={handleChange}
              required
              className="form-control"
            >
              <option value="">Selecciona</option>
              {CATEGORIA_NOTA_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group-inline">
            <label htmlFor="comentario">
              Comentario <span className="required">*</span>
            </label>
            <textarea
              id="comentario"
              name="comentario"
              value={formData.comentario}
              onChange={handleChange}
              required
              rows={2}
              className="form-control"
              placeholder="Describe la interacción..."
            />
          </div>

          <div className="form-group-inline">
            <label htmlFor="proximoPaso">Próximo Paso</label>
            <textarea
              id="proximoPaso"
              name="proximoPaso"
              value={formData.proximoPaso || ''}
              onChange={handleChange}
              rows={2}
              className="form-control"
              placeholder="Acción de seguimiento..."
            />
          </div>

          <div className="form-actions-inline">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Guardando...' : note ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NoteForm;
